import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_EYE_BTN_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import { DEFAULT_PAGE_SIZE, type PageSizeOption } from 'lib/page-size-options';
import CursorPagination from 'components/CursorPagination';
import {
  RefreshCw,
  ScrollText,
  Settings2,
  Eye,
  Save,
  Filter,
  ListChecks,
  Timer,
  Swords,
  X,
} from 'lucide-react';

const ENDPOINT_LABELS: Record<string, string> = {
  user_balance: 'User Balance',
  wallet_debit: 'Debit Request',
  battle_settle: 'Battle Settlement',
  table_leave: 'Leave Table',
  get_battle_settlement: 'Get Battle Settlement',
  get_user_battle_settlement: 'Get User Battle Settlement',
};

interface LogGroup {
  retryGroupId: string;
  endpoint: string;
  consumerCode?: string;
  consumerName?: string;
  battleId?: string;
  transactionId?: string;
  userId?: string;
  totalAttempts: number;
  operationSuccess: boolean;
  canRetry: boolean;
  lastMessage?: string;
  lastBusinessCode?: string;
  lastAttemptAt?: string;
}

interface RetryConfig {
  settleMaxRetries: number;
  settleInitialDelayMs: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const TABLE_COLUMNS = 8;

type TabId = 'logs' | 'settings';

function ConfigField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-muted">{label}</span>
      {children}
      {hint ? <p className="mt-1.5 text-[11px] leading-relaxed text-muted">{hint}</p> : null}
    </label>
  );
}

const inputClass =
  'w-full rounded-lg border border-line bg-card px-3 py-2.5 text-sm text-heading focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/20';

const IntegrationLogs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('logs');
  const [groups, setGroups] = useState<LogGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [endpointFilter, setEndpointFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [config, setConfig] = useState<RetryConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const fetchGroups = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
        });
        if (endpointFilter) params.set('endpoint', endpointFilter);
        if (statusFilter) params.set('status', statusFilter);
        const res = await api.get<{ groups: LogGroup[]; pagination: PaginationMeta }>(
          `${API_ENDPOINTS.ADMIN.INTEGRATION_LOGS.GROUPS}?${params}`
        );
        if (res.success && res.data) {
          setGroups(res.data.groups || []);
          const pagination = res.data.pagination;
          setTotal(pagination?.total ?? 0);
          setTotalPages(Math.max(1, pagination?.totalPages ?? 1));
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, pageSize, endpointFilter, statusFilter]
  );

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const res = await api.get<RetryConfig>(API_ENDPOINTS.ADMIN.INTEGRATION_LOGS.RETRY_CONFIG);
      if (res.success && res.data) setConfig(res.data);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') fetchGroups();
  }, [activeTab, fetchGroups]);

  useEffect(() => {
    if (!loading && totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [loading, page, totalPages]);

  useEffect(() => {
    if (activeTab === 'settings') fetchConfig();
  }, [activeTab, fetchConfig]);

  const saveConfig = async () => {
    if (!config) return;
    setConfigSaving(true);
    setConfigSaved(false);
    try {
      const res = await api.put(API_ENDPOINTS.ADMIN.INTEGRATION_LOGS.RETRY_CONFIG, {
        settleMaxRetries: config.settleMaxRetries,
        settleInitialDelayMs: config.settleInitialDelayMs,
      });
      if (res.success) {
        setConfigSaved(true);
        await fetchConfig();
        window.setTimeout(() => setConfigSaved(false), 3000);
      }
    } finally {
      setConfigSaving(false);
    }
  };

  const hasActiveFilters = Boolean(endpointFilter || statusFilter);
  const isEmpty = !loading && groups.length === 0;

  const clearFilters = () => {
    setEndpointFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const handlePageSizeChange = (limit: PageSizeOption) => {
    setPageSize(limit);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-heading flex items-center gap-2.5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-gold/15 text-brand-gold">
              <ScrollText size={22} />
            </span>
            Integration API Logs
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted leading-relaxed">
            Audit trail for consumer ↔ platform wallet APIs. Each retry session is one group; every HTTP
            attempt is stored separately for debugging and manual retry.
          </p>
        </div>
        {activeTab === 'logs' && (
          <button
            type="button"
            onClick={() => fetchGroups(true)}
            disabled={refreshing}
            className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-line bg-card px-4 py-2.5 text-sm font-semibold text-heading shadow-sm hover:bg-subtle disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-line bg-subtle/60 p-1 w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('logs')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
            activeTab === 'logs'
              ? 'bg-card text-heading shadow-sm'
              : 'text-muted hover:text-heading'
          }`}
        >
          <ListChecks size={16} />
          Log sessions
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('settings')}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
            activeTab === 'settings'
              ? 'bg-card text-heading shadow-sm'
              : 'text-muted hover:text-heading'
          }`}
        >
          <Settings2 size={16} />
          Auto retry
        </button>
      </div>

      {/* ── Logs tab ── */}
      {activeTab === 'logs' && (
        <>
          <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
            <div className="flex flex-col gap-4 border-b border-line px-5 py-4 sm:flex-row sm:items-end sm:px-6">
              <div className="flex items-center gap-2 shrink-0">
                <Filter size={16} className="text-muted" />
                <span className="text-sm font-bold text-heading">Filters</span>
                {hasActiveFilters && (
                  <span className="rounded-full bg-brand-gold/20 px-2 py-0.5 text-[10px] font-bold text-brand-gold">
                    Active
                  </span>
                )}
              </div>
              <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-xl">
                <div>
                  <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-muted">
                    API endpoint
                  </span>
                  <select
                    value={endpointFilter}
                    onChange={(e) => {
                      setEndpointFilter(e.target.value);
                      setPage(1);
                    }}
                    className={inputClass}
                  >
                    <option value="">All APIs</option>
                    {Object.entries(ENDPOINT_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-muted">
                    Outcome
                  </span>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className={inputClass}
                  >
                    <option value="">All outcomes</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-sm font-semibold text-muted hover:bg-subtle hover:text-heading"
                >
                  <X size={14} />
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
            <div className="flex items-center justify-between border-b border-line px-5 py-4 sm:px-6">
              <div>
                <h2 className="font-bold text-heading flex items-center gap-2">
                  <ListChecks className="text-brand-gold" size={18} />
                  Retry sessions
                </h2>
                <p className="text-xs text-muted mt-0.5">
                  {loading
                    ? 'Loading…'
                    : total > 0
                      ? `${total.toLocaleString()} session${total === 1 ? '' : 's'} total${hasActiveFilters ? ' · filtered' : ''}`
                      : hasActiveFilters
                        ? 'No matching sessions'
                        : 'Integration log sessions will appear here'}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className={`${LIST_TABLE_CLASS} min-w-[920px]`}>
                <thead className={LIST_THEAD_CLASS}>
                  <tr>
                    <th className={LIST_TH_NO_CLASS}>No.</th>
                    <th className={LIST_TH_CLASS}>API</th>
                    <th className={LIST_TH_CLASS}>Consumer</th>
                    <th className={LIST_TH_CLASS}>Attempts</th>
                    <th className={LIST_TH_CLASS}>Outcome</th>
                    <th className={LIST_TH_CLASS}>Last message</th>
                    <th className={LIST_TH_CLASS}>Last attempt</th>
                    <th className={LIST_TH_CLASS}>Action</th>
                  </tr>
                </thead>
                <tbody className={LIST_TBODY_CLASS}>
                  {loading && groups.length === 0 ? (
                    <tr className="border-t border-line">
                      <td colSpan={TABLE_COLUMNS} className="px-5 py-16 text-center">
                        <RefreshCw className="mx-auto h-7 w-7 animate-spin text-brand-gold" />
                        <p className="mt-3 text-sm font-medium text-muted">Loading log sessions…</p>
                      </td>
                    </tr>
                  ) : isEmpty ? (
                    <tr className="border-t border-line">
                      <td colSpan={TABLE_COLUMNS} className="px-5 py-16 text-center">
                        <p className="text-sm font-semibold text-muted">Data not available</p>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={clearFilters}
                            className="mt-4 inline-flex items-center gap-2 rounded-xl border border-line px-4 py-2 text-sm font-semibold text-heading hover:bg-subtle"
                          >
                            Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    groups.map((g, index) => (
                      <tr
                        key={g.retryGroupId}
                        className="border-t border-line transition-colors hover:bg-subtle/60"
                      >
                        <td className={`${LIST_TD_NO_CLASS}`}>
                          {listRowNumber(page, index, pageSize)}
                        </td>
                        <td className={LIST_TD_CLASS}>
                          <div className="font-semibold text-heading">
                            {ENDPOINT_LABELS[g.endpoint] || g.endpoint}
                          </div>
                          {g.battleId && (
                            <div className="mt-0.5 max-w-[200px] truncate font-mono text-[11px] text-muted">
                              {g.battleId}
                            </div>
                          )}
                        </td>
                        <td className={LIST_TD_CLASS}>{g.consumerName || g.consumerCode || '—'}</td>
                        <td className={`${LIST_TD_CLASS} tabular-nums font-medium`}>{g.totalAttempts}</td>
                        <td className={LIST_TD_CLASS}>
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                              g.operationSuccess
                                ? 'bg-emerald-500/15 text-emerald-700'
                                : 'bg-red-500/15 text-red-700'
                            }`}
                          >
                            {g.operationSuccess ? 'Success' : 'Failed'}
                          </span>
                        </td>
                        <td className={`${LIST_TD_CLASS} max-w-[220px] truncate text-muted`}>
                          {g.lastMessage || g.lastBusinessCode || '—'}
                        </td>
                        <td className={`${LIST_TD_CLASS} whitespace-nowrap text-muted text-xs`}>
                          {g.lastAttemptAt ? new Date(g.lastAttemptAt).toLocaleString() : '—'}
                        </td>
                        <td className={LIST_TD_CLASS}>
                          <button
                            type="button"
                            onClick={() => navigate(`/integration-logs/${g.retryGroupId}`)}
                            title="View log detail"
                            aria-label="View log detail"
                            className={LIST_EYE_BTN_CLASS}
                          >
                            <Eye size={16} strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {(total > 0 || groups.length > 0) && (
              <CursorPagination
                page={page}
                totalPages={totalPages}
                total={total}
                itemCount={groups.length}
                limit={pageSize}
                loading={loading || refreshing}
                onFirst={() => setPage(1)}
                onPageChange={setPage}
                onLast={() => setPage(totalPages)}
                onLimitChange={handlePageSizeChange}
              />
            )}
          </div>
        </>
      )}

      {/* ── Settings tab ── */}
      {activeTab === 'settings' && (
        <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
          <div className="border-b border-line px-5 py-4 sm:px-6">
            <h2 className="font-bold text-heading flex items-center gap-2">
              <Timer className="text-brand-gold" size={18} />
              Battle settlement auto retry
            </h2>
            <p className="text-xs text-muted mt-1 max-w-2xl">
              Controls how game-backend retries failed Battle Settlement pushes to the consumer before
              giving up or falling back to reconciliation.
            </p>
          </div>

          {configLoading || !config ? (
            <div className="flex h-48 items-center justify-center text-muted">
              <RefreshCw className="mr-2 animate-spin" size={18} />
              Loading settings…
            </div>
          ) : (
            <div className="p-5 sm:p-6 space-y-6">
              <section>
                <div className="mb-4 flex items-center gap-2">
                  <Swords size={16} className="text-brand-gold" />
                  <h3 className="text-sm font-bold text-heading">Battle Settlement</h3>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-2xl">
                  <ConfigField label="Max retries" hint="Extra attempts after the first failure (exponential backoff).">
                    <input
                      type="number"
                      min={0}
                      max={50}
                      className={inputClass}
                      value={config.settleMaxRetries}
                      onChange={(e) => setConfig({ ...config, settleMaxRetries: Number(e.target.value) })}
                    />
                  </ConfigField>
                  <ConfigField label="Initial delay (ms)" hint="Wait before the first retry; doubles each attempt.">
                    <input
                      type="number"
                      min={100}
                      className={inputClass}
                      value={config.settleInitialDelayMs}
                      onChange={(e) => setConfig({ ...config, settleInitialDelayMs: Number(e.target.value) })}
                    />
                  </ConfigField>
                </div>
              </section>

              <div className="flex flex-wrap items-center gap-3 border-t border-line pt-6">
                <button
                  type="button"
                  disabled={configSaving}
                  onClick={saveConfig}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-gold px-5 py-2.5 text-sm font-bold text-brand-dark hover:opacity-95 disabled:opacity-50"
                >
                  <Save size={16} />
                  {configSaving ? 'Saving…' : 'Save settings'}
                </button>
                {configSaved && (
                  <span className="text-sm font-semibold text-emerald-600">Settings saved</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IntegrationLogs;
